import { Fragment, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { CircularProgress } from '@mui/material';
import { useLanguage } from 'src/LanguageContext';
import DropDown from 'src/layouts/full/horizontal/DropDown';
import { translate } from 'src/translate';
import MaterialDropDown from 'src/layouts/full/horizontal/MaterialDropDown';
import SentryLogger from 'src/components/logging/SentryLogger';

// Modify the component to accept props for dynamic content and actions
export default function AlertStyled({
  isLoading,
  setLoading,
  title,
  description,
  primaryButtonLabel,
  secondaryButtonLabel,
  onPrimaryButtonClick,
  onSecondaryButtonClick,
  open,
  setOpen,
  checkboxLabel,
  isCheckboxChecked,
  onCheckboxChange,
  extraCheckboxLabel,
  extraCheckboxAmount,
  isExtraCheckboxChecked,
  onExtraCheckboxChange,
  setCancelReason,
  didntAttendBool = false,
}) {
  const cancelButtonRef = useRef(null);
  const { language, isLTR } = useLanguage();

  // Handle primary button click with proper async/await
  const handlePrimaryClick = async () => {
    try {
      // If the handler returns a boolean, use it to determine if we should close
      // If it returns false explicitly, don't close the dialog
      const result = await onPrimaryButtonClick();
      if (result !== false) {
        setOpen(false);
      }
    } catch (error) {
      console.error('Error in primary button handler:', error);
      const sentryLogger = new SentryLogger();
      sentryLogger.debug(`Error in primary button handler: ${error.message}`);
      // Don't close the dialog on error
    }
  };

  // Handle secondary button click
  const handleSecondaryClick = async () => {
    try {
      // If the handler returns a boolean, use it to determine if we should close
      // If it returns false explicitly, don't close the dialog
      const result = await onSecondaryButtonClick();
      if (result !== false) {
        setOpen(false);
      }
    } catch (error) {
      console.error('Error in secondary button handler:', error);
      const sentryLogger = new SentryLogger();
      sentryLogger.debug(`Error in secondary button handler: ${error.message}`);
      setOpen(false); // Usually want to close on cancel even if there's an error
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[1600]"
        initialFocus={cancelButtonRef}
        onClose={setOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full justify-center p-2 text-center items-center ">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-0 scale-95"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-0 scale-95"
            >
              <Dialog.Panel
                dir={isLTR() ? 'ltr' : 'rtl'}
                className={`relative transform overflow-visible rounded-lg bg-white px-4 pb-4 pt-5 shadow-xl transition-all my-8 w-full max-w-lg p-6 ${
                  language === 'ar' ? 'font-cairo' : ''
                } ${isLTR() ? 'text-left' : 'text-right'}`}
              >
                {
                  <>
                    <div className="">
                      <div className="mt-0 w-full">
                        <Dialog.Title
                          as="h3"
                          className="text-lg font-medium leading-6 text-gray-900"
                        >
                          {title}
                        </Dialog.Title>
                        <div className="mt-2 space-y-4">
                          <p className="text-sm text-gray-500">{description}</p>

                          {didntAttendBool && (
                            <div className="w-full pt-1">
                              <MaterialDropDown
                                title={translate('cancellationReason', language)}
                                choosenStaffId={'didntAttend'}
                                updateStaff={setCancelReason}
                                staffList={[
                                  { id: 'didntAttend', name: 'didntAttend' },
                                  { id: 'other', name: 'other' },
                                ]}
                                needsTranslation={true}
                              />
                            </div>
                          )}

                          {checkboxLabel && (
                            <label className="flex items-start gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={isCheckboxChecked}
                                onChange={onCheckboxChange}
                                className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-watsPrimary focus:ring-watsPrimary"
                              />
                              <span className="min-w-0 leading-5">{checkboxLabel}</span>
                            </label>
                          )}

                          {extraCheckboxLabel && (
                            <label className="flex items-start gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={isExtraCheckboxChecked}
                                onChange={onExtraCheckboxChange}
                                className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-watsPrimary focus:ring-watsPrimary"
                              />
                              <span className="min-w-0 leading-5">
                                {String(extraCheckboxLabel).includes('{amount}') ? (
                                  <>
                                    {String(extraCheckboxLabel).split('{amount}')[0]}
                                    <span dir="ltr" className="inline-block font-medium">
                                      {extraCheckboxAmount != null && extraCheckboxAmount !== ''
                                        ? `₪${extraCheckboxAmount}`
                                        : ''}
                                    </span>
                                    {String(extraCheckboxLabel).split('{amount}')[1]}
                                  </>
                                ) : (
                                  extraCheckboxLabel
                                )}
                              </span>
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="mt-6 flex justify-start gap-2">
                      {isLoading ? (
                        <div className="flex justify-center items-center">
                          <CircularProgress />
                        </div>
                      ) : (
                        primaryButtonLabel && (
                          <button
                            type="button"
                            className="inline-flex justify-center whitespace-nowrap rounded-md  bg-watsPrimary px-4 py-2 text-sm font-medium text-white shadow-sm"
                            onClick={handlePrimaryClick}
                          >
                            {primaryButtonLabel}
                          </button>
                        )
                      )}
                      {!isLoading && secondaryButtonLabel && (
                        <button
                          type="button"
                          className={`inline-flex justify-center whitespace-nowrap rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50`}
                          onClick={handleSecondaryClick}
                          ref={cancelButtonRef}
                        >
                          {secondaryButtonLabel}
                        </button>
                      )}
                    </div>
                  </>
                }
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
